import { useQuery, QueryResult } from '@apollo/client';
import { notNil } from '@sixfold/typed-primitives';
import { useParams, useNavigate } from 'react-router';

import { PlatformMutations } from './platform_mutations_container';
import { CompanyPlatform } from '../../company';
import {
  CompanyPlatformQuery as CompanyPlatformQueryResult,
  CompanyPlatformQueryVariables,
  CompanyPlatformsWithPlatformsQuery as CompanyPlatformsWithPlatformsQueryResult,
  CompanyPlatformsWithPlatformsQueryVariables,
  getNodes,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { PlatformView, PlatformViewDataProps } from '../components/platform';
import { companyPlatformsWithPlatforms, getCompanyPlatformQuery } from '../graphql';

const isCompanyPlatformQueryResult = (queryResult: unknown): queryResult is Partial<CompanyPlatformQueryResult> => {
  return (queryResult as CompanyPlatformQueryResult).companyPlatform !== undefined;
};

const isCompanyPlatformsWithPlatformsQueryResult = (
  queryResult: unknown,
): queryResult is Partial<CompanyPlatformsWithPlatformsQueryResult> => {
  const result = queryResult as CompanyPlatformsWithPlatformsQueryResult;
  return result.platforms !== undefined && result.company !== undefined;
};

class LoadingContainer extends Loader<
  CompanyPlatformQueryResult | CompanyPlatformsWithPlatformsQueryResult,
  PlatformViewDataProps,
  CompanyPlatformQueryVariables | CompanyPlatformsWithPlatformsQueryVariables
> {}

export const PlatformContainer = () => {
  const { platform_id, company_id } = useParams() as { company_id: string; platform_id: string };
  const navigate = useNavigate();
  const isNewPlatform = platform_id === 'new';

  const companyPlatformsWithPlatformsResult = useQuery<
    CompanyPlatformsWithPlatformsQueryResult,
    CompanyPlatformsWithPlatformsQueryVariables
  >(companyPlatformsWithPlatforms, {
    fetchPolicy: 'cache-and-network',
    variables: { company_id },
    skip: !isNewPlatform,
  });

  const companyPlatformResult = useQuery<CompanyPlatformQueryResult, CompanyPlatformQueryVariables>(
    getCompanyPlatformQuery,
    {
      skip: isNewPlatform,
      variables: {
        company_id,
        platform_id,
      },
    },
  );

  return (
    <PlatformMutations>
      {({ createCompanyPlatform, updateCompanyPlatform, deleteCompanyPlatform }) => (
        <LoadingContainer
          result={
            (isNewPlatform ? companyPlatformsWithPlatformsResult : companyPlatformResult) as QueryResult<
              CompanyPlatformQueryResult | CompanyPlatformsWithPlatformsQueryResult,
              CompanyPlatformQueryVariables | CompanyPlatformsWithPlatformsQueryVariables
            >
          }
          mapData={({ data }) => ({
            data: {
              companyPlatform:
                isCompanyPlatformQueryResult(data) && data?.companyPlatform !== undefined ? data.companyPlatform : null,
              platforms: getNodes(isCompanyPlatformsWithPlatformsQueryResult(data) ? data.platforms : undefined),
              companyPlatforms:
                isCompanyPlatformsWithPlatformsQueryResult(data) && notNil(data?.company?.platforms)
                  ? (data?.company?.platforms as unknown as CompanyPlatform[])
                  : [],
            },
          })}>
          {({ data }) => (
            <PlatformView
              createCompanyPlatform={createCompanyPlatform}
              updateCompanyPlatform={updateCompanyPlatform}
              deleteCompanyPlatform={deleteCompanyPlatform}
              data={data}
              isNewPlatform={isNewPlatform}
              routeProps={{
                goBack: () => navigate(-1),
                company_id,
                platform_id,
              }}
            />
          )}
        </LoadingContainer>
      )}
    </PlatformMutations>
  );
};
